import { get, map, mapValues } from 'lodash'
import { getReq } from '../../utils/request'
import {
	FinishesPerMonthOriginTableItem,
	IPriceOffersStatusPerMonthPayload,
	IPriceOffersSentPerMonthPayload,
	ISuccessRatePerPersonPayload,
	PriceOffersPerMonthOriginTableItem,
	SuccessRatePerPersonOriginTableItem
} from '../../reducers/statistics/actions'

const normalizeQueryParams = (params: any = {}) => mapValues({
	limit: 20,
	page: 1,
	...params
}, (query) => query || undefined)

export const fetchFinishesPerMonthStats = async (params: any = {}) => {
	const { data } = await getReq('/api/admin/statistics/finishes-per-month', normalizeQueryParams(params))
	const originalData: FinishesPerMonthOriginTableItem[] = get(data, 'data', [])
	const tableData: FinishesPerMonthOriginTableItem[] = map(originalData, (column) => ({
		count: column.count,
		date: column.date
	}))

	return { finishesPerMonth: tableData }
}

export const fetchPriceOffersSentPerMonthStats = async (params: any = {}) => {
	const { data } = await getReq('/api/admin/statistics/price-offers-sent-per-month', normalizeQueryParams(params))
	const originalData: PriceOffersPerMonthOriginTableItem[] = get(data, 'data', [])
	const tableData: PriceOffersPerMonthOriginTableItem[] = map(originalData, (column) => ({
		count: column.count,
		date: column.date
	}))

	return {
		priceOffersSentPerMonth: tableData
	} as IPriceOffersSentPerMonthPayload
}

export const fetchSuccessRatePerPersonStats = async (params: any = {}) => {
	const { data } = await getReq('/api/admin/statistics/success-rate-per-person', normalizeQueryParams(params))
	const originalData: SuccessRatePerPersonOriginTableItem[] = get(data, 'data', [])
	const tableData: SuccessRatePerPersonOriginTableItem[] = map(originalData, (column) => ({
		userId: column.userId,
		sent: column.sent,
		completed: column.completed,
		successRate: column.successRate
	}))

	return {
		successRatePerPerson: tableData
	} as ISuccessRatePerPersonPayload
}

export const fetchPriceOffersStatusPerMonthStats = async (params: any = {}) => {
	const normalizeStatusQueryParams = mapValues(params, (query) => query || undefined)
	const { data } = await getReq('/api/admin/statistics/price-offers-status-per-month', normalizeStatusQueryParams)

	const originalData: any[] = get(data, 'data', [])
	const context: any = get(data, 'context', null)
	const tableData = originalData.map((item) => ({
		date: item.date,
		FINISHED: item.FINISHED || 0,
		NOT_ACCEPTED: item.NOT_ACCEPTED || 0,
		DECLINED: item.DECLINED || 0,
		PRICE_INCREASE: item.PRICE_INCREASE || 0,
		PRICE_INCREASE_FINISHED: item.PRICE_INCREASE_FINISHED || 0,
		PRICE_INCREASE_DECLINED: item.PRICE_INCREASE_DECLINED || 0,
		details: item.details || {
			FINISHED: [],
			NOT_ACCEPTED: [],
			DECLINED: [],
			PRICE_INCREASE: [],
			PRICE_INCREASE_FINISHED: [],
			PRICE_INCREASE_DECLINED: []
		}
	}))

	return {
		priceOffersStatusPerMonth: tableData,
		context: {
			NOT_ACCEPTED: context?.NOT_ACCEPTED,
			FINISHED: context?.FINISHED,
			DECLINED: context?.DECLINED,
			PRICE_INCREASE: context?.PRICE_INCREASE,
			PRICE_INCREASE_FINISHED: context?.PRICE_INCREASE_FINISHED,
			PRICE_INCREASE_DECLINED: context?.PRICE_INCREASE_DECLINED
		}
	} as IPriceOffersStatusPerMonthPayload
}

interface IJobsLifecycleDetail {
	jobId: number
	address: string
	organizationId: number
	organizationName: string
	createdAt?: string
	ceasedAt?: string
	reason?: string
}

export interface IJobsLifecyclePerMonthItem {
	month: string
	totalActiveJobsNow: number
	totalActiveJobsMonthEnd: number
	createdJobs: number
	createdDetails: IJobsLifecycleDetail[]
	ceasedJobs: number
	ceasedDetails: IJobsLifecycleDetail[]
}

export interface IJobsLifecyclePerMonthPayload {
	jobsLifecyclePerMonth: IJobsLifecyclePerMonthItem[]
	context: {
		totalCount: number
		totalActiveJobsNow: number
	}
}

export interface ISutazItem {
	jobId: number
	jobAddress: string
	expectedDate: string
	actualDate: string | null
	category: 'NACAS' | 'V_ROZMEDZI' | 'MESKAJUCE' | 'NEUPRATANAT' | 'NAPLANOVANE'
	rating: number | null
	ratingComment: string | null
	finishId: number | null
	tasks: { id: number; name: string }[]
}

export interface ISutazRatingItem {
	ratingId: number
	rating: number
	comment: string | null
	jobAddress: string
	actualDate: string
}

export interface ISutazEmployeeResult {
	userId: number
	fullName: string
	organizationNames: string[]
	total: number
	nacas: { count: number; items: ISutazItem[] }
	vRozmedzi: { count: number; items: ISutazItem[] }
	meskajuce: { count: number; items: ISutazItem[] }
	neupratanat: { count: number; items: ISutazItem[] }
	naplanovane: { count: number; items: ISutazItem[] }
	ratingsByScore: Record<number, { count: number; items: ISutazRatingItem[] }>
	goodTaskRatings: { id: number; name: string; count: number }[]
	badTaskRatings: { id: number; name: string; count: number }[]
}

export interface ISutazOdovzdavaniaNacasPayload {
	data: ISutazEmployeeResult[]
	context: {
		totalEmployees: number
	}
}

export const fetchJobsLifecyclePerMonthStats = async (params: {
	organizationIds: number[]
	monthsBack: number
}) => {
	const normalizeJobsLifecycleQueryParams = mapValues(params, (query) => query || undefined)
	const { data } = await getReq('/api/admin/statistics/jobs-lifecycle-per-month', normalizeJobsLifecycleQueryParams)

	const originalData: any[] = get(data, 'data', [])
	const context = get(data, 'context', {
		totalCount: 0,
		totalActiveJobsNow: 0
	})

	const tableData: IJobsLifecyclePerMonthItem[] = originalData.map((item) => ({
		month: item.month,
		totalActiveJobsNow: item.totalActiveJobsNow || 0,
		totalActiveJobsMonthEnd: item.totalActiveJobsMonthEnd || 0,
		createdJobs: item.createdJobs || 0,
		createdDetails: item.createdDetails || [],
		ceasedJobs: item.ceasedJobs || 0,
		ceasedDetails: item.ceasedDetails || []
	}))

	return {
		jobsLifecyclePerMonth: tableData,
		context: {
			totalCount: context.totalCount || 0,
			totalActiveJobsNow: context.totalActiveJobsNow || 0
		}
	} as IJobsLifecyclePerMonthPayload
}

export const fetchSutazOdovzdavaniaNacas = async (params: {
	organizationIds: number[]
	userId?: number | null
	month: string
}) => {
	const queryParams: Record<string, unknown> = {
		organizationIds: params.organizationIds,
		month: params.month
	}

	if (params.userId) {
		queryParams.userId = params.userId
	}

	const { data } = await getReq('/api/admin/statistics/sutaz-odovzdavanie-nacas', queryParams)

	return {
		data: get(data, 'data', []),
		context: {
			totalEmployees: get(data, 'context.totalEmployees', 0)
		}
	} as ISutazOdovzdavaniaNacasPayload
}
